import React, { useState, useEffect, useMemo } from 'react';
import {
  Heading,
  VStack,
  HStack,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Card,
  CardBody,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  useToast,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { api } from '../services/api';

interface Class {
  id: number;
  name: string;
}

const ClassesPage = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');

  // Filter classes based on search term
  const filteredClasses = useMemo(() => {
    if (!searchTerm) return classes;
    return classes.filter((classItem) =>
      classItem.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [classes, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const classesData = await api.getClasses();
      setClasses(classesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const classData = {
        name: formData.name,
      };

      if (editingClass) {
        await api.updateClass(editingClass.id, classData);
        toast({
          title: 'Success',
          description: 'Class updated successfully',
          status: 'success',
          duration: 3000,
        });
      } else {
        await api.createClass(classData);
        toast({
          title: 'Success',
          description: 'Class created successfully',
          status: 'success',
          duration: 3000,
        });
      }
      
      resetForm();
      onClose();
      loadData();
    } catch (error: any) {
      console.error('Error saving class:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save class',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this class?')) {
      try {
        await api.deleteClass(id);
        toast({
          title: 'Success',
          description: 'Class deleted successfully',
          status: 'success',
          duration: 3000,
        });
        loadData();
      } catch (error) {
        console.error('Error deleting class:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete class',
          status: 'error',
          duration: 3000,
        });
      }
    }
  };

  const handleEdit = (classItem: Class) => {
    setEditingClass(classItem);
    setFormData({
      name: classItem.name,
    });
    onOpen();
  };

  const resetForm = () => {
    setFormData({ name: '' });
    setEditingClass(null);
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between">
        <Heading size="xl" color="gray.700">
          🎓 Classes Management
        </Heading>
        <Button colorScheme="purple" onClick={() => { resetForm(); onOpen(); }}>
          ➕ Add Class
        </Button>
      </HStack>

      {/* Search Bar */}
      <Card bg={cardBg} shadow="sm">
        <CardBody>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Text>🔍</Text>
            </InputLeftElement>
            <Input
              placeholder="Search classes by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg="white"
            />
          </InputGroup>
          {searchTerm && (
            <Text fontSize="sm" color="gray.500" mt={2}>
              Found {filteredClasses.length} of {classes.length} classes
            </Text>
          )}
        </CardBody>
      </Card>

      {/* Classes Table */}
      <Card bg={cardBg} shadow="md">
        <CardBody>
          {loading ? (
            <Text>Loading...</Text>
          ) : filteredClasses.length === 0 ? (
            searchTerm ? (
              <Alert status="info">
                <AlertIcon />
                No classes found matching "{searchTerm}". Try a different search term.
              </Alert>
            ) : (
              <Alert status="info">
                <AlertIcon />
                No classes found. Create your first class!
              </Alert>
            )
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Class Name</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredClasses.map((classItem) => (
                  <Tr key={classItem.id}>
                    <Td fontWeight="medium">{classItem.name}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<Text>✏️</Text>}
                          aria-label="Edit"
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={() => handleEdit(classItem)}
                        />
                        <IconButton
                          icon={<Text>🗑️</Text>}
                          aria-label="Delete"
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDelete(classItem.id)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingClass ? 'Edit Class' : 'Create New Class'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Class Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Class 1, Grade 9, etc."
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" colorScheme="purple" isLoading={loading}>
                {editingClass ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default ClassesPage; 